import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '@/lib/api-client'
import { track, getAttribution, hasAnalyticsConsent } from '@/lib/analytics'
import type {
  WalletBalanceResponse,
  SubscriptionResponse,
  SubscriptionPlanResponse,
  CreditPackageResponse,
  CreditTransactionResponse,
  CheckoutSessionResponse,
  WalletActivityRow,
} from '@aegis/zod-schemas'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface TransactionsFilters {
  type?: string
  dateFrom?: string
  dateTo?: string
}

interface CursorPage<T> {
  data: T[]
  nextCursor?: string | null
  hasMore: boolean
}

// ── Wallet ────────────────────────────────────────────────────────────────────

export function useWallet(enabled = true) {
  return useQuery<WalletBalanceResponse>({
    queryKey: ['billing', 'wallet'],
    queryFn: () => apiFetch<WalletBalanceResponse>('/payments/wallet'),
    staleTime: 30_000,
    enabled,
  })
}

export function useTransactions(filters?: TransactionsFilters) {
  return useInfiniteQuery<CursorPage<CreditTransactionResponse>>({
    queryKey: ['billing', 'transactions', filters],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams()
      if (filters?.type && filters.type !== 'all') params.set('type', filters.type)
      if (filters?.dateFrom) params.set('dateFrom', filters.dateFrom)
      if (filters?.dateTo) params.set('dateTo', filters.dateTo)
      if (pageParam) params.set('cursor', pageParam as string)
      params.set('limit', '50')
      const qs = params.toString()
      return apiFetch<CursorPage<CreditTransactionResponse>>(
        `/payments/wallet/transactions${qs ? `?${qs}` : ''}`
      )
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.nextCursor : undefined,
    staleTime: 30_000,
  })
}

export interface WalletActivityFilters {
  dateFrom?: string
  dateTo?: string
}

export function useWalletActivity(filters?: WalletActivityFilters) {
  return useInfiniteQuery<CursorPage<WalletActivityRow>>({
    queryKey: ['billing', 'activity', filters],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams()
      if (filters?.dateFrom) params.set('dateFrom', filters.dateFrom)
      if (filters?.dateTo) params.set('dateTo', filters.dateTo)
      if (pageParam) params.set('cursor', pageParam as string)
      params.set('limit', '30')
      const qs = params.toString()
      return apiFetch<CursorPage<WalletActivityRow>>(
        `/payments/wallet/activity${qs ? `?${qs}` : ''}`
      )
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.nextCursor : undefined,
    staleTime: 30_000,
  })
}

// ── Subscription ──────────────────────────────────────────────────────────────

export function useSubscription() {
  return useQuery<SubscriptionResponse>({
    queryKey: ['billing', 'subscription'],
    queryFn: () => apiFetch<SubscriptionResponse>('/payments/subscription'),
    staleTime: 60_000,
  })
}

export function useSubscriptionPlans() {
  return useQuery<SubscriptionPlanResponse[]>({
    queryKey: ['billing', 'plans'],
    queryFn: () => apiFetch<SubscriptionPlanResponse[]>('/payments/plans'),
    staleTime: 5 * 60_000,
  })
}

export function useCreateSubscription() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { planId: string; billingCycle?: string; successUrl?: string; cancelUrl?: string }) => {
      // Attribuzione marketing (first-touch da localStorage) nel checkout →
      // metadata Stripe → `payment_success` server-side col cid.
      const attrib = getAttribution()
      return apiFetch<CheckoutSessionResponse>('/payments/subscription', {
        method: 'POST',
        body: JSON.stringify({
          ...data,
          cid: attrib.cid,
          utmSource: attrib.utm_source,
          utmMedium: attrib.utm_medium,
          utmCampaign: attrib.utm_campaign,
          utmContent: attrib.utm_content,
          utmTerm: attrib.utm_term,
          riskBand: attrib.risk_band,
          analyticsConsent: hasAnalyticsConsent(),
        }),
      })
    },
    // Funnel: l'utente avvia il checkout (prima del redirect a Stripe).
    onMutate: (data) => {
      track('checkout_start', { planId: data.planId, billingCycle: data.billingCycle ?? 'MONTHLY' })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] })
    },
  })
}

export function useChangeSubscription() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { planId: string; billingCycle?: string }) =>
      apiFetch<SubscriptionResponse>('/payments/subscription', {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] })
    },
  })
}

export function useCancelSubscription() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiFetch<void>('/payments/subscription', { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] })
    },
  })
}

// ── Operation Costs ───────────────────────────────────────────────────────────

export interface OperationCostItem {
  operationType: string
  creditCost: number
  description: string
}

export function useOperationCosts() {
  return useQuery<OperationCostItem[]>({
    queryKey: ['billing', 'operation-costs'],
    queryFn: () => apiFetch<OperationCostItem[]>('/payments/operation-costs'),
    staleTime: 10 * 60_000,
  })
}

// ── Credit Packages / Top-up ──────────────────────────────────────────────────

export function useCreditPackages() {
  return useQuery<CreditPackageResponse[]>({
    queryKey: ['billing', 'packages'],
    queryFn: () => apiFetch<CreditPackageResponse[]>('/payments/top-up/packages'),
    staleTime: 5 * 60_000,
  })
}

export function useCreateTopUp() {
  return useMutation({
    mutationFn: (data: { packageId: string; successUrl?: string; cancelUrl?: string }) =>
      apiFetch<CheckoutSessionResponse>('/payments/top-up', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  })
}

// ── Sync subscription from Stripe ────────────────────────────────────────────

export function useSyncSubscription() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data?: { checkoutSessionId?: string }) =>
      apiFetch<SubscriptionResponse | null>('/payments/subscription/sync', {
        method: 'POST',
        body: JSON.stringify(data ?? {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing'] })
    },
  })
}

// ── Customer Portal ──────────────────────────────────────────────────────────

export function useCreatePortalSession() {
  return useMutation({
    mutationFn: (data?: { returnUrl?: string }) =>
      apiFetch<{ portalUrl: string }>('/payments/portal/session', {
        method: 'POST',
        body: JSON.stringify(data ?? {}),
      }),
  })
}
